import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSparkles,
  faCircleCheck,
  faSpinnerThird,
  faScrewdriverWrench,
  faCopy,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { LabraToolTipV2 } from '@labrav/react-components'
import { AssistantMarkdown } from '../../AssistantMarkdown'
import { copyToClipboard } from '../../../utils/copyToClipboard'
import { LabraChatMessage } from '../../../modules/labraChat/reducer'
import { FundingProgressCard } from '../FundingProgressCard/FundingProgressCard'
import { OpenUiMessage } from '../openui/OpenUiMessage'
import { ScheduleDetailsCard } from '../ScheduleDetailsCard/ScheduleDetailsCard'
import {
  openLabraChatEmailPreview,
  sendLabraChatPrompt,
} from '../../../modules/labraChat/actions'
import {
  useLabraChatContext,
  useLabraChatDispatch,
} from '../../../modules/labraChat/context'
import { useStyles } from './AssistantBubble.styles'

/**
 * Removes the model's internal `<thinking>...</thinking>` reasoning from the
 * displayed text. Also drops a trailing unclosed `<thinking>` block so partial
 * reasoning doesn't flash while the response is still streaming.
 */
const stripThinking = (text: string): string =>
  text
    .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
    .replace(/<thinking>[\s\S]*$/i, '')
    .replace(/^\s+/, '')

interface AssistantBubbleProps {
  message: LabraChatMessage
}

const AssistantBubbleComponent: React.FC<AssistantBubbleProps> = ({
  message,
}) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const { partnerId } = useLabraChatContext()

  const displayContent = React.useMemo(
    () => stripThinking(message.content),
    [message.content]
  )

  // Per-message copy: copy the rendered response text, flashing a check for 2s.
  const [copied, setCopied] = React.useState(false)
  const handleCopy = React.useCallback(() => {
    copyToClipboard(displayContent)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => undefined)
  }, [displayContent])
  // Path B generative UI: OpenUI-Lang from a `render_ui` tool call this turn,
  // rendered as react-ui components alongside the conversational text.
  const openUiLang = message.openUiLang
  const emailPreview = message.emailPreview
  const scheduleDetails = message.scheduleDetails
  // Until the first answer token (or a rendered visual) arrives, surface what
  // the agent is doing.
  const isWorking =
    message.isStreaming &&
    !displayContent &&
    !openUiLang &&
    !emailPreview &&
    !scheduleDetails
  // Render the whole thing as markdown once finalized, or once the reveal has
  // settled mid-stream (so it isn't held back by a late `done` event).
  const showFullMarkdown = !message.isStreaming || !!message.streamSettled

  // Progressive (Claude-style) reveal while streaming: format every completed
  // line as markdown the moment its newline arrives, and keep the in-progress
  // trailing line as plain text until it completes. Splitting on the last
  // newline means the markdown prefix only changes line-by-line, so the
  // memoized <AssistantMarkdown> re-parses per line rather than per frame.
  const lastNewline = displayContent.lastIndexOf('\n')
  const completedLines =
    lastNewline >= 0 ? displayContent.slice(0, lastNewline) : ''
  const inProgressLine =
    lastNewline >= 0 ? displayContent.slice(lastNewline + 1) : displayContent

  const handleOpenEmailPreview = React.useCallback(() => {
    if (emailPreview) dispatch(openLabraChatEmailPreview(emailPreview))
  }, [dispatch, emailPreview])

  const handleSendEmail = React.useCallback(() => {
    if (!emailPreview) return
    const to = emailPreview.recipientEmail
    const prompt = to
      ? `Yes, send the preview email to ${to} (draft_id: ${emailPreview.draftId})`
      : `Yes, send the preview email (draft_id: ${emailPreview.draftId}). Ask me for the recipient if needed.`
    dispatch(sendLabraChatPrompt(partnerId, prompt))
  }, [dispatch, emailPreview, partnerId])

  // The streamed answer body (generative UI + response bubble + inline tool row
  // + copy), shared between the plain content layout and the funding-progress
  // layout. Null until there's a visual or user-facing text to show.
  const hasBody =
    !!displayContent || !!openUiLang || !!emailPreview || !!scheduleDetails

  const responseBody = hasBody ? (
    <>
      {scheduleDetails ? <ScheduleDetailsCard details={scheduleDetails} /> : null}
      {openUiLang && (
        <div className={classes.responseBubble}>
          <OpenUiMessage
            response={openUiLang}
            isStreaming={message.isStreaming}
          />
        </div>
      )}
      {displayContent && (
        <div className={classes.responseBubble}>
          {showFullMarkdown ? (
            <AssistantMarkdown content={displayContent} />
          ) : (
            <>
              {completedLines && (
                <AssistantMarkdown content={completedLines} />
              )}
              {inProgressLine && (
                <div className={classes.streamingTail}>{inProgressLine}</div>
              )}
            </>
          )}
        </div>
      )}
      {emailPreview ? (
        <div className={classes.previewChipRow}>
          <button
            type="button"
            className={classes.previewChip}
            onClick={handleOpenEmailPreview}
            data-testid="chat-email-preview-chip"
          >
            Preview
          </button>
          <button
            type="button"
            className={classes.previewChip}
            onClick={handleSendEmail}
            data-testid="chat-email-send-chip"
            disabled={!!message.isStreaming}
          >
            Yes, send it
          </button>
        </div>
      ) : null}
      {message.isStreaming && message.activeTool && (
        <div className={classes.toolRow}>
          <FontAwesomeIcon
            icon={faScrewdriverWrench}
            fade
            className={classes.stageSpinner}
          />
          <span className={classes.stageLabelActive}>
            {message.activeTool}
          </span>
        </div>
      )}
      {!message.isStreaming && displayContent ? (
        <div className={classes.copyRow}>
          <LabraToolTipV2
            tooltipPlacement="top"
            description={copied ? 'Copied!' : 'Copy'}
          >
            <button
              type="button"
              className={classes.copyButton}
              onClick={handleCopy}
              aria-label="Copy"
            >
              <FontAwesomeIcon
                icon={copied ? faCheck : faCopy}
                className={cx(copied && classes.copiedIcon)}
              />
            </button>
          </LabraToolTipV2>
        </div>
      ) : null}
    </>
  ) : null

  return (
    <div className={classes.row}>
      <div className={classes.sparklesContainer}>
        <FontAwesomeIcon icon={faSparkles} className={classes.sparklesIcon} />
      </div>

      {message.fundingProgress ? (
        // A funding run owns the turn: show the determinate stepper card (in
        // place of the generic spinner), with the final answer below once it
        // streams in.
        <div className={classes.contentCol}>
          <FundingProgressCard progress={message.fundingProgress} />
          {responseBody}
        </div>
      ) : isWorking ? (
        <div className={classes.stageCard}>
          {message.completedStages.map((cs, idx) => (
            <div
              key={idx}
              className={cx(classes.stageRow, classes.stageRowDone)}
            >
              <FontAwesomeIcon
                icon={faCircleCheck}
                className={classes.stageDoneIcon}
              />
              <span>{cs.message}</span>
            </div>
          ))}
          <div className={classes.stageRow}>
            {message.activeTool ? (
              <FontAwesomeIcon
                icon={faScrewdriverWrench}
                fade
                className={classes.stageSpinner}
              />
            ) : (
              <FontAwesomeIcon
                icon={faSpinnerThird}
                spin
                className={classes.stageSpinner}
              />
            )}
            <span className={classes.stageLabelActive}>
              {message.activeTool || message.stageMessage || 'Working'}
            </span>
          </div>
        </div>
      ) : hasBody ? (
        <div className={classes.contentCol}>{responseBody}</div>
      ) : (
        // Finalized with no user-facing text (e.g. the model only produced
        // internal reasoning or a tool call that returned nothing).
        <div className={classes.emptyNote}>
          No response was generated. Please try rephrasing your request.
        </div>
      )}
    </div>
  )
}

// Memoized so a per-frame update to the streaming message doesn't re-render
// (and re-parse markdown for) every other bubble in the thread.
export const AssistantBubble = React.memo(AssistantBubbleComponent)
